import React, { FC, useState } from 'react';
import { AxiosError } from 'axios';
import { unstable_batchedUpdates } from 'react-dom';
import { isEmpty } from '@dxb-web-ui-framework/common/utils';
import { invokeService } from '@dxb-web-ui-framework/common/hooks';
import { APIGatewayResponse } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/DataService/types';
import { GenericObject } from '@dxb-web-ui-framework/common/types';
import {
  Box,
  FileItem,
  FormHelperText,
  BoxProps,
  FileItemProps,
  FileUploader,
  makeStyles,
  Theme,
} from '@dxb-web-ui-framework/design-system';
import { useTranslation } from 'react-i18next';
import { TransferServiceImpl } from '../../../../../fundTransfer/src/domain/Transfer/usecases';
import { ErrorAlert } from '../ErrorAlert/ErrorAlert';

export interface FileType {
  file: File;
  fileId: number;
}

export interface FileUploadProps {
  label: string;
  description: string;
  type: string;
  inputProps?: GenericObject;
  boxProps?: BoxProps;
  fileItemProps?: FileItemProps;
  service: TransferServiceImpl;
  transactionId: number;
  shouldUploadDocuments: boolean;
  formBuilderProps?: GenericObject;
  preselectedFileItems?: FileType[];
  isLoading?: boolean;
  externalFilesSetter?: React.Dispatch<React.SetStateAction<GenericObject>>;
  isFileItemAction: boolean;
}
const supportedFiles =
  /(apng|APNG|bmp|BMP|x-icon|gif|GIF|cur|CUR|jpg|JPG|jpeg|JPEG|jfif|JFIF|pjpeg|PJPEG|pjp|PJP|png|PNG|svg\+xml|tif|TIF|tiff|TIFF|webp|WEBP|pdf|PDF|sheet|vnd.ms-excel)/;

const supportedFileName = /^\s*[a-zA-Z0-9-!@#$%^&*()_+=\s]*\s*$/;

const base64Formatter = (value: string) => {
  const base64Index = value.indexOf('base64,');
  return value.substring(base64Index + 7);
};

const convertBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);

    fileReader.onload = () => {
      resolve(fileReader.result as string);
    };

    fileReader.onerror = error => {
      reject(error);
    };
  });

const useStyles = makeStyles<Theme>(theme => ({
  root: {
    fontSize: '1rem',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.9rem',
    },
  },
}));

const FileUpload: FC<FileUploadProps> = ({
  label,
  description,
  type,
  inputProps,
  boxProps,
  fileItemProps,
  service,
  transactionId,
  children,
  shouldUploadDocuments,
  formBuilderProps,
  preselectedFileItems,
  externalFilesSetter,
  isLoading,
  isFileItemAction,
}) => {
  const [isPostingData, setIsPostingData] = useState(false);
  const [postDataError, setPostDataError] = useState<AxiosError<APIGatewayResponse<any>, any> | undefined>(undefined);
  const [selectedFiles, setSelectedFiles] = useState<FileType[] | []>(preselectedFileItems ?? []);
  const [selectedFileError, setSelectedFileError] = useState<string | undefined>();
  const { t } = useTranslation();
  const classes = useStyles();

  const fileSetterHandler = (file: FileType) => {
    setSelectedFiles(prev => [...prev, file]);
    if (formBuilderProps && formBuilderProps.setValue) {
      formBuilderProps.setValue('uploadedDocs', [...selectedFiles, file]);
    }

    if (externalFilesSetter && formBuilderProps) {
      const values = formBuilderProps.getValues();

      externalFilesSetter(prev => ({
        ...prev,
        ...values,
        uploadedDocs: [...selectedFiles, file],
      }));
    }
  };

  const deleteFileHandler = (fileId: number) => {
    const remainingSelectedFiles = selectedFiles.filter(selectedFile => selectedFile.fileId !== fileId);
    if (formBuilderProps && formBuilderProps.setValue) {
      formBuilderProps.setValue('uploadedDocs', remainingSelectedFiles);
    }

    if (externalFilesSetter && formBuilderProps) {
      const values = formBuilderProps.getValues();
      externalFilesSetter(prev => ({
        ...prev,
        ...values,
        uploadedDocs: remainingSelectedFiles,
      }));
    }
    setSelectedFiles(remainingSelectedFiles);
  };

  const onPostDocument = async (file: File) => {
    setIsPostingData(true);
    const fileContentToBase64 = await convertBase64(file);
    const { error } = await invokeService<any>(() =>
      service.PostDocument(
        {
          transactionId,
          fileName: file.name,
          fileDescription: file.name,
          fileData: base64Formatter(fileContentToBase64),
        },
        (response: any) => {
          unstable_batchedUpdates(() => {
            fileSetterHandler({ fileId: response.data.fileId, file });
            setIsPostingData(false);
            setPostDataError(undefined);
          });
        },
      ),
    );

    if (error && !isEmpty(error)) {
      unstable_batchedUpdates(() => {
        setPostDataError(error);
        setIsPostingData(false);
      });
    }
  };

  const onDeleteDocument = async (file: File, fileId: number) => {
    const isAbleToDeleteDoc = shouldUploadDocuments;
    if (isAbleToDeleteDoc) {
      setIsPostingData(true);
      const fileContentToBase64 = await convertBase64(file);
      const { error } = await invokeService<any>(() =>
        service.DeleteDocument(
          {
            transactionId,
            fileName: file.name,
            fileId: fileId as number,
            fileDescription: file.name,
            fileData: base64Formatter(fileContentToBase64),
          },
          () => {
            unstable_batchedUpdates(() => {
              setIsPostingData(false);
              deleteFileHandler(fileId);
            });
          },
        ),
      );
      if (error && !isEmpty(error)) {
        unstable_batchedUpdates(() => {
          setPostDataError(error);
          setIsPostingData(false);
        });
      }
    } else {
      deleteFileHandler(fileId);
    }
  };

  const fileValidatorHandler = (file?: File): { isAbleToSetDoc: boolean } => {
    if (!file) {
      return { isAbleToSetDoc: false };
    }
    const isValidFileType = file.type.match(supportedFiles);
    if (!isValidFileType && !file.name.includes('.cur')) {
      setSelectedFileError(t('ContentComposer.FileUploader.FileType.Validation.Message'));
      return { isAbleToSetDoc: false };
    }
    const fileNameWithoutExtension = file.name.split('.').slice(0, -1).join('.');
    const matches = fileNameWithoutExtension.match(supportedFileName);

    if (matches?.[0] !== fileNameWithoutExtension) {
      setSelectedFileError(t('ContentComposer.FileUploader.UnsupportedFileName.Validation.Message'));
      return { isAbleToSetDoc: false };
    }

    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > 5) {
      setSelectedFileError(t('ContentComposer.FileUploader.MaximumLimit.Validation.Message'));
      return { isAbleToSetDoc: false };
    }
    return { isAbleToSetDoc: true };
  };

  const onInputFileChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    if (e.target?.files) {
      const { isAbleToSetDoc } = fileValidatorHandler(e.target.files[0]);
      if (shouldUploadDocuments && isAbleToSetDoc) {
        onPostDocument(e.target.files[0]);
        e.target.value = '';
        return;
      }
      if (isAbleToSetDoc) {
        fileSetterHandler({ fileId: e.target.files.length + 1, file: e.target.files[0] });
      }
      e.target.value = '';
    }
  };

  const onDrop: React.DragEventHandler<HTMLElement> = e => {
    e.preventDefault();
    if (e.dataTransfer.files) {
      fileValidatorHandler(e.dataTransfer.files[0]);
      const { isAbleToSetDoc } = fileValidatorHandler(e.dataTransfer.files[0]);
      if (shouldUploadDocuments && isAbleToSetDoc) {
        onPostDocument(e.dataTransfer.files[0]);
        return;
      }
      if (isAbleToSetDoc) {
        fileSetterHandler({ fileId: e.dataTransfer.files.length + 1, file: e.dataTransfer.files[0] });
      }
      e.dataTransfer.clearData();
    }
  };

  const onDragOver: React.DragEventHandler<HTMLElement> = e => {
    setSelectedFileError(undefined);
    e.preventDefault();
  };

  const onClickFileUploader: React.MouseEventHandler<HTMLElement> = () => {
    const fileInput = document.getElementById('fileInput');
    if (fileInput) {
      setSelectedFileError(undefined);
      fileInput.click();
    }
  };

  const renderError = () => {
    const isError = Boolean(selectedFileError);
    const FormHelperTextProps = () =>
      !isError
        ? undefined
        : {
            id: `error`,
            'data-testid': `error`,
            role: 'alert',
          };
    if (!isError) {
      return null;
    }

    return (
      <FormHelperText classes={{ root: classes.root }} error={isError} {...FormHelperTextProps()}>
        {selectedFileError}
      </FormHelperText>
    );
  };

  const isDisabled = formBuilderProps?.isSubmitting || isPostingData || isLoading;
  const isClickActionEnabled = isFileItemAction && (!(isPostingData || isLoading) || !isEmpty(postDataError));

  return (
    <Box {...boxProps}>
      <input
        id='fileInput'
        type='file'
        name='fileInput'
        hidden={true}
        onChange={onInputFileChange}
        disabled={isDisabled}
        {...inputProps}
      />
      <FileUploader
        description={description}
        label={label}
        type={type}
        loading={isPostingData}
        disabled={isDisabled}
        onDrop={onDrop}
        onClick={onClickFileUploader}
        onDragOver={onDragOver}>
        <>
          {postDataError && !isEmpty(postDataError) && (
            <Box mt={3}>
              <ErrorAlert message={postDataError.message} />
            </Box>
          )}
          {renderError()}
          {isEmpty(selectedFiles)
            ? null
            : selectedFiles.map(({ fileId, file }) => (
                <Box mt={1} key={fileId}>
                  <FileItem
                    state={
                      isClickActionEnabled
                        ? [
                            {
                              type: 'close',
                              onClick: () => onDeleteDocument(file, fileId),
                              title: t('FileItem.Delete.Action'),
                            },
                          ]
                        : []
                    }
                    filename={file.name}
                    {...fileItemProps}
                  />
                </Box>
              ))}
          {children && children}
        </>
      </FileUploader>
    </Box>
  );
};

export { FileUpload };
